/**
 * API client.
 *
 * `credentials: 'include'` on every call is what carries the vendor session
 * cookie. In production the portal and the API are the same origin so this is a
 * no-op; in local dev Vite proxies /api to :4000 and it matters.
 */

export class ApiError extends Error {
  readonly status: number;
  /** Machine-readable code from the API, e.g. 'rate_limited_email'. */
  readonly code: string;
  readonly body: Record<string, unknown>;

  constructor(status: number, code: string, body: Record<string, unknown>) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

/** Thrown when the request never reached the server — offline, DNS, dropped LTE. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'NetworkError';
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { token?: string } = {},
): Promise<T> {
  const { token, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (rest.body !== undefined) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(path, { ...rest, headers, credentials: 'include' });
  } catch (err) {
    // A vendor at a gate loses signal mid-flow often enough that this needs to
    // be a distinct, recoverable state rather than a generic failure.
    throw new NetworkError(err);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let body: Record<string, unknown> = {};
  if (text) {
    try {
      body = JSON.parse(text) as Record<string, unknown>;
    } catch {
      body = { raw: text };
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, String(body.error ?? `http_${res.status}`), body);
  }
  return body as T;
}

// ─────────────────────────────────────────────────────────── vendor surface

export interface SiteInfo {
  id: string;
  name: string;
  label: string | null;
  addressLine: string | null;
  timezone: string;
  /**
   * GET /site/:siteId: signing in here emails the property team — notices are
   * on and the site resolves at least one recipient right now. The form says
   * so before the vendor signs in.
   */
  notifiesProperty?: boolean;
}

export interface OpenVisit {
  id: string;
  name: string;
  company: string;
  purpose: string;
  signedInAt: string;
  signedInAtLocal: string;
}

export interface MeResponse {
  email: string;
  site: SiteInfo;
  openVisit: OpenVisit | null;
  lastVisit: { name: string; company: string } | null;
}

export interface VisitPayload {
  id: string;
  name: string;
  company: string;
  purpose: string;
  signedInAtLocal: string;
  dateLine: string;
  /**
   * When a visit left open is signed out by the 7 PM sweep: "7:00 PM", or
   * "7:00 PM tomorrow" after hours. Optional: an API from before it says nothing.
   */
  autoSignOutLocal?: string;
  site: SiteInfo;
}

/** 201 signed_in, or 200 already_open, whose body property notices did not change. */
export type CheckinResponse =
  | {
      status: 'signed_in';
      visit: VisitPayload;
      /** True only when at least one property notice was queued for this sign-in. */
      propertyNotified?: boolean;
    }
  | { status: 'already_open'; visit: VisitPayload };

/**
 * The most a vendor can type into name, company and the "Other" purpose.
 *
 * ⚠️ MIRRORED from api/src/services/visits.ts (VISIT_TEXT_MAX) — the widths of
 * the nvarchar columns. Check-in refuses anything longer with 400 `too_long`
 * rather than cutting it short, so the form stops typing here first.
 */
export const VISIT_TEXT_MAX = { name: 200, company: 200, purpose: 200 } as const;

export const vendorApi = {
  getSite: (siteId: string) =>
    request<{ site: SiteInfo }>(`/api/vendor/site/${encodeURIComponent(siteId)}`),

  requestCode: (siteId: string, email: string) =>
    request<{ status: 'sent' | 'session' }>('/api/vendor/code', {
      method: 'POST',
      body: JSON.stringify({ siteId, email }),
    }),

  verify: (siteId: string, email: string, code: string) =>
    request<{ status: 'verified'; expiresAt: string }>('/api/vendor/verify', {
      method: 'POST',
      body: JSON.stringify({ siteId, email, code }),
    }),

  me: () => request<MeResponse>('/api/vendor/me'),

  checkin: (input: { name: string; company: string; purposeKey: string; otherText?: string }) =>
    request<CheckinResponse>('/api/vendor/checkin', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  checkout: () =>
    request<{
      status: 'signed_out';
      receiptSent: boolean;
      visit: {
        id: string;
        name: string;
        company: string;
        purpose: string;
        signedInAtLocal: string;
        signedOutAtLocal: string;
        dateLine: string;
        site: SiteInfo;
      };
    }>('/api/vendor/checkout', { method: 'POST' }),
};

// ─────────────────────────────────────────────── property notices and flags

/** Who a property notice went to. */
export type NoticeRole = 'property_manager' | 'assistant_manager' | 'site_inbox';

/** A role as people read it, on the flag page and on the staff log. */
export const NOTICE_ROLE_LABEL: Record<NoticeRole, string> = {
  property_manager: 'Property manager',
  assistant_manager: 'Assistant manager',
  site_inbox: 'Site inbox',
};

/** A visit's first flag, as the flag page sees it. */
export interface FlagSummary {
  /** "10:35 AM", or "Oct 14, 10:35 AM" when flagged on a later day than the sign-in. */
  atLocal: string;
  /** Null for the site inbox, which is nobody in particular. */
  byName: string | null;
  byRole: NoticeRole;
  /**
   * The visit's first flag — the one whose write won the visit, and the one
   * the senior property manager's email names — came from the recipient of
   * the link this page was opened from.
   */
  byYou: boolean;
}

/** How a visit ended: signed out by the vendor, closed by the evening sweep, or closed by staff. */
export type ClosedReason = 'vendor' | 'auto_end_of_day' | 'staff';

/** POST /api/flag/lookup. */
export interface FlagLookup {
  site: { name: string; label: string | null };
  visit: {
    name: string;
    company: string;
    purpose: string;
    signedInAtLocal: string;
    dateLine: string;
    onSite: boolean;
    /** Dated ("Oct 14, 7:00 PM") when it is not the sign-in's local day. */
    signedOutAtLocal: string | null;
    /** Null while on site — or for a reason this page does not know. */
    closedReason: ClosedReason | null;
  };
  recipient: { role: NoticeRole };
  /**
   * A flag from this link would email a senior property manager right now.
   * False also when that cannot be told yet: the page then promises nothing
   * — and never says nobody is listed.
   */
  seniorListed: boolean;
  /** The visit's FIRST flag, from any recipient; null while nobody has flagged it. */
  flag: FlagSummary | null;
  expiresAt: string;
}

/** POST /api/flag. */
export interface FlagResult {
  /** already_flagged: THIS notice had flagged the visit already — a double tap. */
  status: 'flagged' | 'already_flagged';
  /** The visit's first flag after this call, which need not be this one. */
  flag: FlagSummary;
  /**
   * The email to the senior property manager(s), from THIS request:
   *   attempted  this was the visit's first flag and the site has, or may
   *              have, a senior to tell. False: a later flag (the first one
   *              carried the email), or no senior is listed.
   *   notified   who was emailed.
   *   failed     they could not be emailed — the send failed, or they could
   *              not be looked up right now — so the flagger should call.
   * attempted, nobody notified, nothing failed: the flagger is the only
   * senior listed.
   */
  escalation: { attempted: boolean; notified: string[]; failed: boolean };
}

/**
 * The flag page's two calls, both public. The link's token travels in the
 * POST body and nowhere else: a path or a query string ends up in the access
 * logs and App Insights' request URLs, which is exactly what keeping it in
 * the link's #fragment avoids. lookup never changes anything — mail scanners
 * open links — and only flag records one.
 */
export const flagApi = {
  lookup: (noticeId: string, mac: string) =>
    request<FlagLookup>('/api/flag/lookup', {
      method: 'POST',
      body: JSON.stringify({ noticeId, mac }),
    }),

  flag: (noticeId: string, mac: string) =>
    request<FlagResult>('/api/flag', {
      method: 'POST',
      body: JSON.stringify({ noticeId, mac }),
    }),

  /**
   * The page opened with a fragment it cannot read. Carries NO part of it —
   * only that it happened — so ops can see mail mangling links; the answer
   * is the same 400 any unreadable link gets, and is ignored.
   */
  reportMalformed: () =>
    request<never>('/api/flag/lookup', {
      method: 'POST',
      body: JSON.stringify({ malformed: true }),
    }),
};

// ──────────────────────────────────────────────────────────── staff surface

export interface AdminVisit {
  id: string;
  name: string;
  email: string;
  company: string;
  purpose: string;
  purposeKey: string;
  signedInAtLocal: string;
  signedOutAtLocal: string | null;
  closedReason: string | null;
  onSite: boolean;
  /** What became of the property notice for this sign-in. */
  notice: {
    /** null: the visit is older than property notices. */
    state: 'queued' | 'no_recipients' | 'disabled' | 'suppressed' | 'error' | null;
    sent: number;
    pending: number;
    failed: number;
  };
  /** null until a notice recipient flags the sign-in. */
  flag: {
    flaggedAt: string;
    flaggedAtLocal: string;
    /** Every recipient who flagged it, oldest first. */
    by: { name: string | null; role: NoticeRole; atLocal: string }[];
  } | null;
}

export interface VisitsResponse {
  site: { id: string; name: string; label: string | null; timezone: string };
  date: string;
  /** May this viewer sign a vendor out here (listed at the site, or holding visits.close)? */
  canClose?: boolean;
  visits: AdminVisit[];
  kpis: {
    onSite: number;
    visitsToday: number;
    /** Null when nothing has closed yet — render an em dash, never a 0. */
    avgVisitMinutes: number | null;
    companies: number;
  };
}

export interface AdminSite {
  id: string;
  name: string;
  label: string | null;
  city: string | null;
  state: string | null;
  timezone: string;
  /** 'east' | 'west' — the DM assignment's region (2502). Null when none is on file. */
  region?: string | null;
  /** The property's district: its DM. Null when none is on file. */
  district?: { id: number | null; name: string | null } | null;
  /** Why the viewer sees it: "Property manager", "District manager"… Empty for a whole-portfolio view. */
  relations?: string[];
  /** May the viewer sign a vendor out here? */
  canClose?: boolean;
}

/** What the viewer's reach is: the widest thing they hold. */
export type ScopeKind = 'all' | 'region' | 'district' | 'property' | 'none';

/** Why a viewer sees nothing: the property lists are off, still loading or too old, wise-iam is down, or they are not listed. */
export type EmptyReason = 'lists_off' | 'lists_loading' | 'lists_stale' | 'iam_unavailable' | 'not_listed';

export interface StaffMe {
  upn: string;
  name: string | null;
  permissions: string[];
  /** Anything in scope at all. */
  canViewVisits: boolean;
  canCloseVisits: boolean;
  canPrintPosters: boolean;
  /** wise-iam did not answer: only the properties the viewer is listed for are shown. */
  iamUnavailable?: boolean;
  /** Does a Sign out email the vendor right now (SIGNOUT_EMAILS, a sender)? */
  vendorEmailsOnSignOut?: boolean;
  scope?: {
    kind: ScopeKind;
    /** Why nothing is in scope; null when something is. */
    emptyReason?: EmptyReason | null;
    sites: number;
    listedSites: number;
    regions: { key: string; label: string }[];
    districts: { id: number; name: string | null }[];
  };
}

export interface DashboardCounts {
  onSite: number;
  today: number;
  last7Days: number;
  flagged30Days: number;
}

export interface DashboardSite extends AdminSite, DashboardCounts {
  lastSignInAt: string | null;
  /** "9:05 AM" today, else "Oct 12, 9:05 AM" — in the site's zone. */
  lastSignInLocal: string | null;
}

export interface DashboardDistrict {
  id: number | null;
  /** The DM's name. Null for properties with no district on file. */
  name: string | null;
  totals: DashboardCounts;
  sites: DashboardSite[];
}

export interface DashboardRegion {
  key: string | null;
  label: string;
  totals: DashboardCounts;
  districts: DashboardDistrict[];
}

export interface OnSiteNow {
  id: string;
  siteId: string;
  siteName: string;
  name: string;
  company: string;
  purpose: string;
  signedInAt: string;
  signedInAtLocal: string;
  flagged: boolean;
  canClose: boolean;
}

export interface DashboardResponse {
  generatedAt: string;
  /** When a vendor who forgets is signed out, facility-local: "7:00 PM". */
  autoSignOutAt?: string;
  scope: { kind: ScopeKind; regions: { key: string; label: string }[]; districtIds: number[] };
  totals: DashboardCounts & { sites: number };
  regions: DashboardRegion[];
  onSiteNow: OnSiteNow[];
}

export interface CloseResult {
  /** Null when the sign-out took but reading it back failed: reload the log. */
  visit: AdminVisit | null;
  /** The vendor's "you were signed out" email was queued. */
  vendorEmailed?: boolean;
  /** Sign-out emails are on at all. */
  vendorEmailsOn?: boolean;
}

export const adminApi = {
  me: (token: string) => request<StaffMe>('/api/admin/me', { token }),

  dashboard: (token: string) => request<DashboardResponse>('/api/admin/dashboard', { token }),

  sites: (token: string) =>
    request<{ sites: AdminSite[]; posterBaseUrl: string | null }>('/api/admin/sites', { token }),

  visits: (token: string, siteId: string, date?: string) =>
    request<VisitsResponse>(
      `/api/admin/visits?siteId=${encodeURIComponent(siteId)}${date ? `&date=${date}` : ''}`,
      { token },
    ),

  closeVisit: (token: string, id: string) =>
    request<CloseResult>(`/api/admin/visits/${encodeURIComponent(id)}/close`, {
      method: 'POST',
      token,
    }),

  /**
   * The day's log as a CSV file. Fetched with the bearer token and handed back
   * as a Blob: the route is token-gated like every admin route, and a plain
   * link navigation sends no Authorization header, so it got a 401.
   */
  csv: async (
    token: string,
    siteId: string,
    date: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const path = `/api/admin/visits.csv?siteId=${encodeURIComponent(siteId)}&date=${encodeURIComponent(date)}`;
    let res: Response;
    try {
      res = await fetch(path, {
        headers: { authorization: `Bearer ${token}` },
        credentials: 'include',
      });
    } catch (err) {
      throw new NetworkError(err);
    }
    if (!res.ok) throw new ApiError(res.status, `http_${res.status}`, {});
    const disposition = res.headers.get('content-disposition') ?? '';
    const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'vendor-log.csv';
    return { blob: await res.blob(), filename };
  },
};
